// Print a vessel's Vessel Entry & Work Control Form (ΕΝΤΥΠΟ ΕΙΣΟΔΟΥ ΣΚΑΦΟΥΣ & ΕΛΕΓΧΟΥ ΕΡΓΑΣΙΩΝ) as the
// customer's Word form looks on paper: one A4 page (8 mm top and bottom, 9 mm at the sides), Arial at the
// file's sizes, the navy section bars, the light cell borders and the same four-column table with the same
// spans. Section 2 is the revised arrival inspection as its own Word file lays it out: the A / B / C bars,
// the two check tables (item, result, comment; a tinted heading row and alternate rows) and Photos. What was
// saved prints in place of the blanks (☒ for a ticked box, the chosen OK / DEF / N/T / N/A or Yes / No,
// dates in the person's date format, line breaks kept); whatever was left empty keeps its ____ line, so a
// printed copy can still be finished by hand. With no record it is the blank paper form. The small
// TECHNOHULL logo sits at the top left, as on the app's other printed documents (boatDocPrint.ts).
// Printed through the same window as the boat yard forms (printWindow.ts), from the click itself so the
// pop-up is not blocked, phones included; fitToOnePage keeps a long form on its one sheet.

import type { BoatDocRecord, CheckResult, InspectionCheck } from '@technohull/shared';
import { controlOf } from './vesselEntry.js';
import { printInWindow } from './printWindow.js';
import {
  VEF_DISCLAIMER, VEF_ENGINES_NOTE, VEF_FUNCTIONAL, VEF_LABELS as L, VEF_PARTS as P, VEF_PHOTOS, VEF_RESULTS, VEF_SECTIONS as S, VEF_TITLE,
  VEF_VISUAL, biText, type Bi,
} from './vesselEntryCopy.js';

/** how dates print: the person's date format (the app's date formatter). */
export interface VesselEntryPrintFormat {
  date: (iso: string) => string;
}

/** what the printout reads from a record (a saved one, or what is on screen in the dialog). */
export type VesselEntryPrintRecord = Pick<BoatDocRecord, 'boatName' | 'model' | 'sap' | 'entryDate' | 'exitDate' | 'control'>;

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// the paper form's blanks, character for character
const U = (n: number) => '_'.repeat(n);
const DATE_BLANK = '____ / ____ / ______';
const BOX = '☐';
const TICK = '☒';

/** a label as the paper prints it, "Greek / English:", each half in its own language. */
const lbl = (b: Bi, colon = true) =>
  `<span lang="el">${esc(b.el)}</span> / <span lang="en">${esc(b.en)}</span>${b.tail ? ` / <span lang="el">${esc(b.tail)}</span>` : ''}${colon ? ':' : ''}`;
/** a filled value, or the blank it stands in for. */
const val = (v: string, blank: string, cls = 'u') => (v.trim() ? `<span class="v">${esc(v.trim())}</span>` : `<span class="${cls}">${blank}</span>`);
const box = (on: boolean) => `<span class="bx">${on ? TICK : BOX}</span>`;

/** about how many printed lines a text takes in a box `perLine` characters wide (its own breaks kept). */
const linesOf = (text: string, perLine: number) =>
  text.split('\n').reduce((n, line) => n + Math.max(1, Math.ceil(line.length / perLine)), 0);
/** a text written one item per line, run on as one paragraph (its lines joined by " · "). */
const runOn = (t: string) => t.split('\n').map((l) => l.trim()).filter(Boolean).join(' · ');
/** a free-text box: what was written, then the ____ lines it leaves (all of them when nothing was). A text
 *  whose own line breaks would take it past the box's lines (a list, one item a line) runs on instead, so
 *  it takes the lines its words need rather than a line per item, and the form keeps its size. */
function writtenLines(text: string, lines: number, blank: string, perLine: number): string {
  let t = text.replace(/\r\n?/g, '\n').replace(/\s+$/, '');
  if (linesOf(t, perLine) > lines && linesOf(runOn(t), perLine) < linesOf(t, perLine)) t = runOn(t);
  const left = t ? Math.max(0, lines - linesOf(t, perLine)) : lines;
  return `${t ? `<div class="v pre">${esc(t)}</div>` : ''}${Array.from({ length: left }, () => `<div class="ln">${blank}</div>`).join('')}`;
}

/** a check's OK / DEF on one line and N/T / N/A on the next, as the paper has them, the chosen one ticked. */
const results = (r: CheckResult | '') => {
  const one = (x: (typeof VEF_RESULTS)[number]) => `<span class="${r === x.value ? 'on' : ''}">${box(r === x.value)} ${esc(x.mark)}</span>`;
  return `${VEF_RESULTS.slice(0, 2).map(one).join('    ')}<br>${VEF_RESULTS.slice(2).map(one).join('    ')}`;
};

/** one of the arrival check's tables: its heading row, then each item (Greek in bold, the English under it),
 *  its result and its comment (or the comment's blank). */
function checkTable<K extends string>(rows: readonly { key: K; label: Bi }[], values: Record<K, InspectionCheck>): string {
  const head = [L.item, L.result, L.comment].map((b) => `<td>${lbl(b, false)}</td>`).join('');
  const body = rows.map(({ key, label }, i) => {
    const row = values[key];
    return `<tr${i % 2 ? ' class="z"' : ''}><td class="it"><b lang="el">${esc(label.el)}</b><br><span class="en" lang="en">${esc(label.en)}</span></td>`
      + `<td class="rs">${results(row.result)}</td><td class="cm">${val(row.comment, U(10), 'u0')}</td></tr>`;
  }).join('');
  return `<table class="ck"><colgroup><col class="c1"><col class="c2"><col class="c3"></colgroup><tbody><tr class="ckh">${head}</tr>${body}</tbody></table>`;
}

/** the filled form (or, with no record, the blank one) as a standalone A4 page. */
export function vesselEntryHtml(rec: VesselEntryPrintRecord | null, fmt: VesselEntryPrintFormat): string {
  const c = controlOf({ control: rec?.control ?? null });
  const day = (iso: string | undefined) => (iso ? fmt.date(iso) : '');
  const logo = `${window.location.origin}/technohull-logo-doc.png`;
  const name = rec?.boatName.trim() ?? '';
  const typeModelSap = rec ? [rec.model.trim(), rec.sap.trim()].filter(Boolean).join(' / ') : '';

  const bar = (b: Bi) => `<tr class="h6"><td colspan="4" class="b bar">${lbl(b, false)}</td></tr>`;
  const dateCell = (b: Bi, iso: string | undefined, cls = 'fld s8') =>
    `<td class="b ${cls}">${lbl(b)}<br>${val(day(iso), DATE_BLANK, 'u6')}</td>`;
  const tickCell = (b: Bi, on: boolean, span: number, cls = 'ck s75') =>
    `<td${span > 1 ? ` colspan="${span}"` : ''} class="b ${cls}">${box(on)} ${lbl(b, false)}</td>`;

  const legend = VEF_RESULTS.filter((x) => x.inLegend).map((x) => `${esc(x.mark)} = ${lbl(x.meaning, false)}`).join('   •   ');
  const photos = `${VEF_PHOTOS.map((p) => `${box(c.photos === p.value)} <span lang="en">${esc(p.label.en)}</span>`).join('   ')}  /  `
    + VEF_PHOTOS.map((p) => `${box(c.photos === p.value)} <span lang="el">${esc(p.label.el)}</span>`).join('    ');
  const part = (b: Bi) => `<tr><td colspan="4" class="part">  ${lbl(b, false)}</td></tr>`;
  const line = (b: Bi, v: string, blank: number, span = 2) => `<td colspan="${span}" class="b fld s8">${lbl(b)} ${val(v, U(blank), 'u0')}</td>`;

  return `<!doctype html><html lang="el"><head><meta charset="utf-8">
<title>${esc(biText(VEF_TITLE))}${name ? ` - ${esc(name)}` : ''}</title>
<style>
  @page{size:A4 portrait;margin:8mm 9mm}
  *{box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  html,body{margin:0;padding:0}
  body{background:#e8eaed;font-family:Arial,"Liberation Sans",Helvetica,sans-serif;font-size:8.5pt;line-height:1.32;color:#222}
  .paper{width:210mm;min-height:297mm;margin:12px auto;padding:8mm 9mm;background:#fff;box-shadow:0 1px 6px rgba(0,0,0,.18)}
  .sheet{width:192mm;margin:0 auto;zoom:var(--fit,1);break-after:avoid;page-break-after:avoid}
  .hd{position:relative;text-align:center;line-height:1.15}
  .hd .logo{position:absolute;left:0;top:50%;height:6mm;width:auto;transform:translateY(-50%)}
  .t1{font-size:12pt;font-weight:bold;color:#1F4E78}
  .t2{font-size:9.5pt;font-weight:bold;color:#4D4D4D;margin-bottom:1pt}
  table.f{width:100%;border-collapse:collapse;table-layout:fixed}
  table.f col{width:25%}
  table.f tr{break-inside:avoid;page-break-inside:avoid}
  table.f td{padding:0 1.9mm;vertical-align:top;border:0;overflow-wrap:anywhere}
  table.f td.b{border:.5pt solid #B8C2CC}
  table.f td.bar{background:#1F4E78;color:#fff;font-weight:bold;font-size:9pt;line-height:1.15;padding:.79mm 1.32mm}
  table.f td.fld{padding:1.06mm 1.23mm .79mm 1.41mm;vertical-align:middle;color:#333;line-height:1.15}
  table.f td.ck{padding:.79mm .97mm .79mm 1.15mm;vertical-align:middle;color:#222;line-height:1.09}
  table.f td.txt{padding:.97mm 1.41mm .79mm 1.41mm;color:#333;line-height:1.035}
  table.f .s8{font-size:8pt} table.f .s75{font-size:7.5pt} table.f .s7{font-size:7pt}
  /* each bordered row's height on the paper: the file's row height plus its cells' top and bottom margins */
  tr.h6{height:7.8mm} tr.h105{height:13mm} tr.h102{height:12.7mm} tr.h10{height:11.8mm} tr.h92{height:11.1mm}
  tr.h165{height:18.3mm} tr.h112{height:13.3mm} tr.h7{height:7.4mm}
  table.f td.disc{padding:1.06mm 1.41mm .79mm 2.2mm;vertical-align:middle;font-size:8pt;line-height:1.25}
  table.f td.disc b{font-size:8.5pt}
  table.f td.legend{padding:1.2mm 1.41mm .4mm;font-size:7.5pt}
  table.f td.part{background:#17475F;color:#fff;font-weight:bold;font-size:8.5pt;line-height:1.2;padding:.9mm 1.32mm;white-space:pre}
  table.f td.nest{padding:.6mm 0 .8mm}
  table.f td.eng{padding:0 1.41mm 1.2mm;font-size:7.5pt;line-height:1.25}
  table.f td.eng b{color:#17475F}
  table.f td.ph{padding:1.4mm 1.41mm .4mm;font-size:8pt}
  table.ck{width:100%;border-collapse:collapse;table-layout:fixed}
  table.ck col.c1{width:59%} table.ck col.c2{width:25.8%} table.ck col.c3{width:15.2%}
  table.ck td{border:.5pt solid #D8E3E8;padding:.55mm 1.5mm .5mm 1.68mm;vertical-align:middle;line-height:1.18;font-size:7.5pt;overflow-wrap:anywhere}
  table.ck tr.ckh td{background:#D8E8F0;font-weight:bold;font-size:7.5pt;padding-top:.8mm;padding-bottom:.8mm}
  table.ck tr.z td{background:#F5F8FA}
  table.ck td.it b{font-size:7.5pt}
  table.ck td.it .en{color:#4C6371;font-size:6.75pt}
  table.ck td.rs{font-size:7.5pt;white-space:pre}
  table.ck td.cm .v{font-size:7pt;font-weight:bold}
  .sp,.legend{white-space:pre-wrap}
  .u{color:#666}.u6{color:#666}.u0{color:inherit}.u7{color:#777;font-size:7.5pt}
  .ln{white-space:nowrap;overflow:hidden}
  .v{color:#000;font-weight:bold}
  .pre{white-space:pre-wrap}
  .bx{font-family:"Segoe UI Symbol","DejaVu Sans","MS Gothic",Arial,sans-serif;font-weight:normal}
  .on{font-weight:bold;color:#000}
  .txt .v{font-size:7.5pt;line-height:1.25;margin-top:.3mm}
  .tight .txt .v{font-size:6.5pt;line-height:1.15}
  .tight table.ck td.cm .v{font-size:6.5pt}
  @media print{
    body{background:#fff}
    .paper{width:auto;min-height:0;margin:0;padding:0;box-shadow:none}
  }
</style></head><body>
<div class="paper"><div class="sheet">
  <div class="hd"><img class="logo" src="${logo}" alt="TECHNOHULL"><div class="t1" lang="el">${esc(VEF_TITLE.el)}</div><div class="t2" lang="en">${esc(VEF_TITLE.en)}</div></div>
  <table class="f"><colgroup><col><col><col><col></colgroup><tbody>
  ${bar(S.vessel)}
  <tr class="h105"><td colspan="2" class="b fld">${lbl(L.vesselName)}<br><br>${val(name, U(30))}</td><td colspan="2" class="b fld">${lbl(L.typeModelSap)}<br><br>${val(typeModelSap, U(30))}</td></tr>
  ${bar(S.arrival)}
  <tr class="h10">${dateCell(L.arrival, rec?.entryDate)}<td colspan="3" class="disc"><b lang="el">${esc(VEF_DISCLAIMER.el)}</b><br><span lang="en">${esc(VEF_DISCLAIMER.en)}</span></td></tr>
  <tr><td colspan="4" class="legend">${legend}</td></tr>
  ${part(P.visual)}
  <tr><td colspan="4" class="nest">${checkTable(VEF_VISUAL, c.visual)}</td></tr>
  <tr><td colspan="4" class="eng"><b>${lbl(VEF_ENGINES_NOTE.label)}</b> <span lang="el">${esc(VEF_ENGINES_NOTE.text.el)}</span><br><span lang="en">${esc(VEF_ENGINES_NOTE.text.en)}</span></td></tr>
  ${part(P.functional)}
  <tr><td colspan="4" class="nest">${checkTable(VEF_FUNCTIONAL, c.functional)}</td></tr>
  ${part(P.record)}
  <tr class="h7">${line(L.inspectedBy, c.inspectedBy, 22)}${line(L.inspectedOn, day(c.inspectedOn), 17)}</tr>
  <tr class="h7">${line(L.technician, c.technician, 19)}${line(L.signature, '', 25)}</tr>
  <tr><td colspan="4" class="ph">${lbl(L.photos)} ${photos}</td></tr>
  ${bar(S.admin)}
  <tr class="h10">${tickCell(L.rolodexEntry, c.rolodexEntry, 2)}${tickCell(L.jobFileOpened, c.jobFileOpened, 1)}${dateCell(L.registeredOn, c.registeredOn, 'fld s75')}</tr>
  <tr class="h102"><td colspan="2" class="b fld">${lbl(L.registeredBy)}<br>${val(c.registeredBy, U(30))}</td><td colspan="2" class="b fld"></td></tr>
  ${bar(S.scope)}
  <tr class="h92">${tickCell(L.workRequestReceived, c.workRequestReceived, 2)}${tickCell(L.scopeIssued, c.scopeIssued, 2)}</tr>
  <tr class="h165"><td colspan="4" class="b txt"><span class="s8">${lbl(L.criticalItems)}</span>${writtenLines(c.criticalItems, 3, `<span class="u7">${U(120)}</span>`, 140)}</td></tr>
  ${bar(S.schedule)}
  <tr class="h112">${dateCell(L.worksCommenced, c.worksCommenced, 'fld s75')}${dateCell(L.estimatedCompletion, c.estimatedCompletion, 'fld s75')}${dateCell(L.handover, rec?.exitDate, 'fld s75')}<td></td></tr>
  </tbody></table>
</div></div>
</body></html>`;
}

/** the A4 page's height inside its 8 mm margins, in CSS pixels (a millimetre short, for rounding). */
export const ONE_PAGE_PX = (280 * 96) / 25.4;

/** keep the printed form to its one A4 page: a long text first prints a size smaller (.tight); if the form
 *  still runs over, the whole page is scaled down until it fits. Run on the print window's page. */
export function fitToOnePage(doc: Document): void {
  const sheet = doc.querySelector<HTMLElement>('.sheet');
  if (!sheet) return;
  const height = () => sheet.getBoundingClientRect().height;
  if (height() > ONE_PAGE_PX) doc.body.classList.add('tight');
  // the sheet's zoom is its --fit (1 unless set here)
  for (let i = 0; i < 6 && height() > ONE_PAGE_PX; i++) {
    const fit = parseFloat(sheet.style.getPropertyValue('--fit') || '1');
    sheet.style.setProperty('--fit', String(Math.floor(((fit * ONE_PAGE_PX) / height()) * 995) / 1000));
  }
}

/** open a print window with the form (filled in, or blank with no record), fit it to its page and open
 *  the print dialog. Returns false when the browser blocked the pop-up, so the caller can say so. */
export function printVesselEntry(rec: VesselEntryPrintRecord | null, fmt: VesselEntryPrintFormat): boolean {
  return printInWindow(vesselEntryHtml(rec, fmt), fitToOnePage);
}
